import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../core/config/firebase';
import { getEasternDate } from '../../wordle/utils/dailyWordUtils';

const COLLECTION_NAME = 'campusGuesserResults';

/**
 * Save a game result to Firestore
 * @param {Object} result - Game result data
 * @param {string} result.userId - User ID
 * @param {string} result.username - Username (denormalized for fast leaderboard queries)
 * @param {number} result.locationId - Location ID for the daily challenge
 * @param {number} result.distance - Distance in meters
 * @param {number} result.score - Calculated score
 * @param {Object} result.guessLocation - {lat, lng}
 * @param {Object} result.actualLocation - {lat, lng}
 * @param {number} result.completionTimeMs - Time taken to complete
 * @returns {Promise<string>} - Document ID
 */
export async function saveGameResult(result) {
  try {
    const docData = {
      userId: result.userId,
      username: result.username, // Denormalized for fast leaderboard queries
      locationId: result.locationId,
      distance: result.distance,
      score: result.score,
      guessLocation: result.guessLocation,
      actualLocation: result.actualLocation,
      completionTimeMs: result.completionTimeMs,
      date: getEasternDate(), // Store as string: YYYY-MM-DD
      timestamp: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), docData);
    console.log('Game result saved:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error saving game result:', error);
    throw error;
  }
}

/**
 * Get all results for a specific location on a specific date
 * @param {number} locationId - Location ID
 * @param {string} date - Date string (YYYY-MM-DD)
 * @returns {Promise<Array>} - Array of results
 */
export async function getResultsForLocation(locationId, date = null) {
  try {
    const targetDate = date || getEasternDate();

    const q = query(
      collection(db, COLLECTION_NAME),
      where('locationId', '==', locationId),
      where('date', '==', targetDate),
      orderBy('distance', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const results = [];

    querySnapshot.forEach((doc) => {
      results.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return results;
  } catch (error) {
    console.error('Error getting results for location:', error);
    throw error;
  }
}

/**
 * Calculate percentile rank for a user's guess
 * @param {number} userDistance - User's distance in meters
 * @param {Array} allResults - All results for the location
 * @returns {number} - Percentile (0-100)
 */
export function calculatePercentileRank(userDistance, allResults) {
  if (!allResults || allResults.length === 0) return 100;

  // Count how many guesses were worse (greater distance) than user's
  const worseGuesses = allResults.filter(result => result.distance > userDistance).length;

  // Percentile = (# of worse guesses / total guesses) × 100
  const percentile = Math.round((worseGuesses / allResults.length) * 100);

  return percentile;
}

/**
 * Calculate average distance for a location
 * @param {Array} allResults - All results for the location
 * @returns {number} - Average distance in meters
 */
export function calculateAverageDistance(allResults) {
  if (!allResults || allResults.length === 0) return 0;

  const totalDistance = allResults.reduce((sum, result) => sum + result.distance, 0);
  return Math.round(totalDistance / allResults.length);
}

/**
 * Get comparison stats for a user's result
 * @param {number} locationId - Location ID
 * @param {number} userDistance - User's distance in meters
 * @param {string} date - Date string (YYYY-MM-DD)
 * @returns {Promise<Object>} - {percentile, averageDistance, totalPlayers}
 */
export async function getComparisonStats(locationId, userDistance, date = null) {
  try {
    const allResults = await getResultsForLocation(locationId, date);

    const percentile = calculatePercentileRank(userDistance, allResults);
    const averageDistance = calculateAverageDistance(allResults);

    return {
      percentile,
      averageDistance,
      totalPlayers: allResults.length
    };
  } catch (error) {
    console.error('Error getting comparison stats:', error);
    // Return default values on error
    return {
      percentile: null,
      averageDistance: null,
      totalPlayers: 0
    };
  }
}
